import { useMemo } from 'react'
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts'
import { AlertCircle } from 'lucide-react'
import { prepareTop20ChartData, getOperatorColors } from '../utils/chartHelpers'

/**
 * Custom tooltip for TOP 20 chart
 */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  
  const data = payload[0].payload
  
  return (
    <div className="bg-white border border-brand-gray-200 rounded-lg shadow-lg p-3">
      <p className="font-semibold text-brand-gray-800 mb-1">{data.fullName}</p>
      <p className="text-sm text-brand-gray-600">
        <span className="font-medium">Plant:</span> {data.plantCode}
      </p>
      <p className="text-sm text-brand-gray-600">
        <span className="font-medium">Miasto:</span> {data.city}
      </p>
      <p className="text-sm font-bold text-brand-blue mt-1">
        Sprzedaż: {data.sales.toLocaleString()} szt.
      </p>
    </div>
  )
}

/**
 * TOP 20 Salons sub-report component
 */
const Top20Salons = ({ 
  data, 
  operator,
  analysisMode,
  analysisPeriod 
}) => {
  const chartData = useMemo(() => {
    return prepareTop20ChartData(data?.salon_data, 20)
  }, [data?.salon_data])

  const colors = getOperatorColors(operator)

  if (!chartData.length) {
    return (
      <div className="bg-white rounded-xl border border-brand-gray-200 shadow-sm p-8">
        <div className="flex flex-col items-center justify-center text-center">
          <AlertCircle className="h-12 w-12 text-amber-500 mb-3" />
          <h3 className="text-lg font-semibold text-brand-gray-800 mb-1">
            Brak danych
          </h3>
          <p className="text-sm text-brand-gray-500">
            Brak danych do wyświetlenia wykresu TOP 20
          </p>
        </div>
      </div>
    )
  }

  const title = analysisMode === 'monthly' 
    ? `TOP 20 - ${analysisPeriod}` 
    : 'TOP 20 - Łączna sprzedaż'

  return (
    <div className="bg-white rounded-xl border border-brand-gray-200 shadow-sm p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-brand-gray-800">{title}</h3>
        <p className="text-sm text-brand-gray-500 mt-1">
          Wykres pokazuje {chartData.length} najlepszych salonów
        </p>
      </div>

      <div className="h-[500px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={chartData} 
            margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis 
              dataKey="name" 
              angle={-45}
              textAnchor="end"
              height={80}
              interval={0}
              tick={{ fontSize: 10, fill: '#6b7280' }}
            />
            <YAxis 
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickFormatter={(value) => value.toLocaleString()}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="sales" 
              fill={colors.primary}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export default Top20Salons
